/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabtled, then this script is never loaded.
 */

(function () {
  function initDotsForCollectionList(root) {
    const isCollage = root.classList.contains('list-collections--collage');
    if (!isCollage) return;

    const scroller = root.querySelector('.container');
    const items = Array.from(root.querySelectorAll('.list-collections__item'));
    const dotsContainer = root.querySelector('.list-collections__dots');
    if (!scroller || !items.length || !dotsContainer) return;

    const dots = Array.from(dotsContainer.querySelectorAll('.list-collections__dot'));
    if (dots.length !== items.length) return;

    const updateActiveDot = () => {
      const scrollerRect = scroller.getBoundingClientRect();
      let activeIndex = 0;
      let minDelta = Infinity;

      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
        const delta = Math.abs(itemCenter - scrollerCenter);
        if (delta < minDelta) {
          minDelta = delta;
          activeIndex = index;
        }
      });

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const target = items[index];
        if (!target) return;

        const offset = target.offsetLeft - scroller.offsetLeft;
        scroller.scrollTo({ left: offset, behavior: 'smooth' });
      });
    });

    scroller.addEventListener('scroll', throttle(updateActiveDot, 100));
    updateActiveDot();
  }

  // Mega-menu visibility management
  function initMegaMenuVisibility() {
    const megaMenus = document.querySelectorAll('.mega-menu');
    
    megaMenus.forEach(megaMenu => {
      // Use MutationObserver to watch for attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
            const isOpen = !megaMenu.hasAttribute('hidden') && !megaMenu.classList.contains('is-closing');
            document.body.classList.toggle('mega-menu-open', isOpen);
          }
        });
      });

      // Also listen for class changes
      const classObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isOpen = !megaMenu.hasAttribute('hidden') && !megaMenu.classList.contains('is-closing');
            document.body.classList.toggle('mega-menu-open', isOpen);
          }
        });
      });

      // Start observing
      observer.observe(megaMenu, { attributes: true, attributeFilter: ['hidden'] });
      classObserver.observe(megaMenu, { attributes: true, attributeFilter: ['class'] });

      // Check initial state
      const isOpen = !megaMenu.hasAttribute('hidden') && !megaMenu.classList.contains('is-closing');
      document.body.classList.toggle('mega-menu-open', isOpen);
    });
  }

  function initAll() {
    document.querySelectorAll('collection-list.list-collections').forEach(initDotsForCollectionList);
    initMegaMenuVisibility();
  }

  // Simple throttle (if theme.js throttle not available globally)
  function throttle(fn, wait) {
    let last = 0;
    let timeout;
    return function throttled(...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        clearTimeout(timeout);
        timeout = undefined;
        last = now;
        fn.apply(this, args);
      } else if (!timeout) {
        timeout = setTimeout(() => {
          last = Date.now();
          timeout = undefined;
          fn.apply(this, args);
        }, remaining);
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run in Theme Editor when section reloads
  document.addEventListener('shopify:section:load', initAll);
})();

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".gemstone-readmore").forEach(function (btn) {
    btn.addEventListener("click", function () {
      const content = this
        .closest(".gemstone-content")
        .querySelector(".gemstone-more-content");

      const isOpen = content.style.display === "block";
      content.style.display = isOpen ? "none" : "block";
      this.textContent = isOpen ? "Read more" : "Read less";
    });
  });
});